/**
 * QR des joueurs sur le menu de la TV, entre deux jeux.
 *
 * Sur le menu, la console n'a plus d'`API` socket montée (ScreenApp démonté). Au moment
 * de quitter le jeu, ScreenApp note donc la session de la soirée TELLE QUE le socket
 * l'a ouverte : numéro, lien exact donné aux joueurs (`API.getSessionLink()`, mêmes
 * `srv` / langue que le QR du jeu), établissement, heure. Le menu ne montre ce lien que :
 * - pour le MÊME établissement (changement d'espace dans l'onglet → rien) ;
 * - si la lib garde toujours cette session comme celle de la TV (`ub_sessionID`,
 *   appareil « 0 ») — elle les efface quand le serveur ferme la session ;
 * - moins de `MENU_QR_MAX_AGE_MS` après la sortie du jeu : le serveur garde une session
 *   de bar 15 min sans TV (grâce « host » en mode anim), on garde 5 min de marge.
 * Sinon le menu dit simplement que le QR s'affichera au lancement du jeu.
 */
export const MENU_QR_MAX_AGE_MS = 10 * 60 * 1000;

const LEFT_GAME_KEY = 'ub_menu_left_game';
const HOST_DEVICE_ID = '0';

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>;

export type MenuSession = { sessionID: string; link: string };

type Stored = MenuSession & { clientID: string; at: number };

function store(): SessionStore | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * À appeler quand la TV quitte un jeu (ScreenApp démonté), avec la session réellement
 * ouverte par le socket. Session pas prête (`null`) → rien de noté, pas de QR au menu.
 */
export function markLeftGame(
    clientID: string,
    session: MenuSession | null,
    now = Date.now(),
    storage: SessionStore | null = store()
): void {
    if (!storage || !session || !/^\d{6}$/.test(session.sessionID)) return;
    // Lien réel du serveur : http(s), contient le numéro, aucun gabarit non rempli.
    if (!/^https?:\/\//.test(session.link) || !session.link.includes(session.sessionID) || session.link.includes('{'))
        return;
    try {
        const value: Stored = { ...session, clientID, at: now };
        storage.setItem(LEFT_GAME_KEY, JSON.stringify(value));
    } catch {
        /* stockage plein ou refusé : pas de QR sur le menu, rien de cassé */
    }
}

function readStored(storage: SessionStore | null): Stored | null {
    try {
        const raw = storage?.getItem(LEFT_GAME_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw) as Partial<Stored>;
        if (typeof v.sessionID !== 'string' || typeof v.link !== 'string') return null;
        if (typeof v.clientID !== 'string' || typeof v.at !== 'number') return null;
        return v as Stored;
    } catch {
        return null;
    }
}

/** Session de la soirée à montrer sur le menu de `clientID`, sinon `null`. */
export function readMenuSession(
    clientID: string,
    now = Date.now(),
    storage: SessionStore | null = store()
): MenuSession | null {
    const v = readStored(storage);
    if (!v || v.clientID !== clientID) return null;
    const age = now - v.at;
    if (age < 0 || age > MENU_QR_MAX_AGE_MS) return null;
    try {
        // Toujours la session de la TV pour la lib (effacée si le serveur l'a fermée).
        if (storage?.getItem('ub_sessionID') !== v.sessionID) return null;
        if (storage?.getItem(`ub_deviceID_${v.sessionID}`) !== HOST_DEVICE_ID) return null;
    } catch {
        return null;
    }
    return { sessionID: v.sessionID, link: v.link };
}

/** Instant où le QR du menu cessera d'être affiché (pour re-rendre pile à ce moment). */
export function menuSessionExpiresAt(storage: SessionStore | null = store()): number | null {
    const v = readStored(storage);
    return v ? v.at + MENU_QR_MAX_AGE_MS : null;
}
